import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as KeyIcon } from '../../assets/icons/key.svg';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import Button from '../Button/Button';
import TableauDeBord from '../TableauDeBord/TableauDeBord';
import SearchBar from '../SearchBar/SearchBar';
import { isPrestataireLibreRole, isAdminRole } from '../../utils/roleUtils';
import './GestionComptesPrestataires.css';

interface PrestataireUser {
  id: number | string;
  idUser: number | string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
  role: string;
  dateMiseAJour: string;
}

const GestionComptesPrestataires: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<PrestataireUser[]>([]);
  const [filteredData, setFilteredData] = useState<PrestataireUser[]>([]);
  const [entiteId, setEntiteId] = useState<string | number>('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isConfirmUpdateModalOpen, setIsConfirmUpdateModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PrestataireUser | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    fonction: ''
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    telephone: ''
  });

  // Déterminer si c'est un prestataire libre
  const isPrestataireLibre = isPrestataireLibreRole(user?.role || '');

  // Charger les données des prestataires
  useEffect(() => {
    const fetchPrestataireUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Token d'authentification non trouvé");

        // ===== PRESTATAIRE LIBRE =====
        if (isPrestataireLibre) {
          console.log('GestionComptesPrestataires - Mode Prestataire Libre');

          // Utiliser la route /profile/team pour les prestataires libres
          const teamResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!teamResponse.ok) {
            const errorData = await teamResponse.json().catch(() => ({}));
            throw new Error(errorData.message || 'Erreur lors de la récupération de l\'équipe');
          }

          const teamData = await teamResponse.json();

          if (teamData.status !== 'success' || !teamData.data?.membres) {
            throw new Error('Format de réponse API inattendu');
          }

          // Transformer les données
          const prestataireUsers: PrestataireUser[] = teamData.data.membres.map((membre: any) => ({
            id: membre.id,
            idUser: membre.user_id,
            nom: membre.nom,
            prenom: membre.prenom,
            email: membre.email,
            telephone: membre.telephone || '',
            fonction: membre.fonction || '',
            role: membre.is_admin ? 'Administrateur' : 'Membre',
            dateMiseAJour: membre.updated_at
              ? new Date(membre.updated_at).toLocaleDateString('fr-FR')
              : '-'
          }));

          setUserData(prestataireUsers);
          setFilteredData(prestataireUsers);
          return;
        }

        // ===== PRESTATAIRE ACCORD CADRE =====
        console.log('GestionComptesPrestataires - Mode Prestataire Accord Cadre');

        // Récupérer l'ID de l'entité de l'utilisateur connecté
        const userEndpoint = getUserEndpoint();
        if (!userEndpoint) {
          throw new Error('Type de rôle non supporté');
        }

        const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/${userEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!meResponse.ok) {
          throw new Error('Erreur lors de la récupération des informations utilisateur');
        }

        const meData = await meResponse.json();
        const entiteIdFromApi = meData.data?.id_entite;

        if (!entiteIdFromApi) {
          throw new Error('ID d\'entité non trouvé');
        }

        setEntiteId(entiteIdFromApi);

        // Récupérer les informations de l'entité avec tous ses utilisateurs
        const entiteResponse = await fetch(`${process.env.REACT_APP_API_URL}/entites/${entiteIdFromApi}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!entiteResponse.ok) {
          throw new Error('Erreur lors de la récupération des utilisateurs');
        }

        const entiteData = await entiteResponse.json();

        if (entiteData.status !== 'success' || !entiteData.data) {
          throw new Error('Format de réponse API inattendu');
        }

        // Transformer les données en liste de TOUS les utilisateurs (admin, signataire, prestataire)
        const prestataireUsers: PrestataireUser[] = [];

        // Ajouter les gestionnaires (Prestataire signataire admin) - en lecture seule
        if (entiteData.data.gestionnaires && entiteData.data.gestionnaires.length > 0) {
          entiteData.data.gestionnaires.forEach((gestionnaire: any) => {
            prestataireUsers.push({
              id: gestionnaire.id,
              idUser: gestionnaire.id_user,
              nom: gestionnaire.nom,
              prenom: gestionnaire.prenom,
              email: gestionnaire.email_contact,
              telephone: gestionnaire.telephone || '',
              fonction: gestionnaire.fonction || '',
              role: 'Prestataire signataire admin',
              dateMiseAJour: new Date(gestionnaire.updated_at || gestionnaire.created_at).toLocaleDateString('fr-FR')
            });
          });
        }

        // Ajouter les signataires (Prestataire signataire) - en lecture seule
        if (entiteData.data.signataires && entiteData.data.signataires.length > 0) {
          entiteData.data.signataires.forEach((signataire: any) => {
            prestataireUsers.push({
              id: signataire.id,
              idUser: signataire.id_user,
              nom: signataire.nom,
              prenom: signataire.prenom,
              email: signataire.email_contact,
              telephone: signataire.telephone || '',
              fonction: signataire.fonction || '',
              role: 'Prestataire signataire',
              dateMiseAJour: new Date(signataire.updated_at || signataire.created_at).toLocaleDateString('fr-FR')
            });
          });
        }

        // Ajouter les interlocuteurs (Prestataire simples) - modifiables
        if (entiteData.data.interlocuteurs && entiteData.data.interlocuteurs.length > 0) {
          entiteData.data.interlocuteurs.forEach((interlocuteur: any) => {
            prestataireUsers.push({
              id: interlocuteur.id,
              idUser: interlocuteur.id_user,
              nom: interlocuteur.nom,
              prenom: interlocuteur.prenom,
              email: interlocuteur.email_contact,
              telephone: interlocuteur.telephone || '',
              fonction: interlocuteur.fonction || '',
              role: 'Prestataire',
              dateMiseAJour: new Date(interlocuteur.updated_at || interlocuteur.created_at).toLocaleDateString('fr-FR')
            });
          });
        }

        setUserData(prestataireUsers);
        setFilteredData(prestataireUsers);

      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        setError(error instanceof Error ? error.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    if (user?.role) {
      fetchPrestataireUsers();
    }
  }, [user?.role, isPrestataireLibre]);

  // Déterminer le endpoint API en fonction du rôle
  const getUserEndpoint = () => {
    const role = user?.role || '';
    switch (role) {
      case 'Prestataire signataire admin':
        return 'gestionnaires/me';
      case 'Prestataire signataire':
        return 'signataires/me';
      case 'Prestataire':
        return 'interlocuteurs/me';
      default:
        return '';
    }
  };

  // Validation email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation téléphone
  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\s+/g, '').replace(/[^0-9]/g, '');
    return /^\d{10}$/.test(cleanPhone);
  };

  // Ouvrir le modal de création
  const handleOpenCreateModal = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      fonction: ''
    });
    setFormErrors({ email: '', telephone: '' });
    setIsCreateModalOpen(true);
  };

  // Ouvrir le modal d'édition
  const handleOpenEditModal = (userToEdit: PrestataireUser) => {
    setSelectedUser(userToEdit);
    setFormData({
      nom: userToEdit.nom,
      prenom: userToEdit.prenom,
      email: userToEdit.email,
      telephone: userToEdit.telephone,
      fonction: userToEdit.fonction
    });
    setFormErrors({ email: '', telephone: '' });
    setIsEditModalOpen(true);
  };

  // Créer un utilisateur
  const handleCreateUser = async () => {
    // Validation
    let hasErrors = false;
    const errors = { email: '', telephone: '' };

    if (!validateEmail(formData.email)) {
      errors.email = 'Email invalide';
      hasErrors = true;
    }

    if (formData.telephone && !validatePhone(formData.telephone)) {
      errors.telephone = 'Le numéro doit contenir 10 chiffres';
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        console.log('Création membre équipe - Mode Prestataire Libre');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            fonction: formData.fonction || null,
          }),
        });

        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || "Erreur lors de la création du membre");
        }

        // Fermer le modal de création et ouvrir le modal de succès
        setIsCreateModalOpen(false);
        setIsSuccessModalOpen(true);

        // Recharger la liste des membres
        window.location.reload();
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      console.log('Création membre équipe - Mode Prestataire Accord Cadre');

      // Mot de passe par défaut (essentiel pour la réinitialisation lors de la validation du mail)
      const defaultPassword = 'password123';

      // Étape 1: Créer l'utilisateur dans la table users avec le token de l'admin
      const registerResponse = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase(),
          password: defaultPassword,
          password_confirmation: defaultPassword,
          telephone: formData.telephone,
          role: 'prestataire'
        }),
      });

      const registerData = await registerResponse.json();
      if (registerData.status !== 'success') {
        throw new Error(registerData.message || "Erreur lors de la création de l'utilisateur");
      }

      // Étape 2: Créer l'entrée interlocuteur
      const interlocuteurResponse = await fetch(`${process.env.REACT_APP_API_URL}/interlocuteurs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${registerData.token}`
        },
        body: JSON.stringify({
          id_entite: entiteId,
          id_user: registerData.user.id,
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          email_contact: formData.email.toLowerCase(),
          fonction: formData.fonction || ''
        }),
      });

      const interlocuteurData = await interlocuteurResponse.json();
      if (interlocuteurData.status !== 'success') {
        throw new Error(interlocuteurData.message || "Erreur lors de la création du profil prestataire");
      }

      // Fermer le modal de création et ouvrir le modal de succès
      setIsCreateModalOpen(false);
      setIsSuccessModalOpen(true);

    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Ouvrir le modal de confirmation avant modification
  const handleConfirmUpdate = () => {
    // Validation
    let hasErrors = false;
    const errors = { email: '', telephone: '' };

    if (!validateEmail(formData.email)) {
      errors.email = 'Email invalide';
      hasErrors = true;
    }

    if (formData.telephone && !validatePhone(formData.telephone)) {
      errors.telephone = 'Le numéro doit contenir 10 chiffres';
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors);
      return;
    }

    // Si pas d'erreur, ouvrir le modal de confirmation
    setIsConfirmUpdateModalOpen(true);
  };

  // Modifier un utilisateur après confirmation
  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            fonction: formData.fonction || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la mise à jour');
        }

        setIsConfirmUpdateModalOpen(false);
        setIsEditModalOpen(false);
        window.location.reload();
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      const response = await fetch(`${process.env.REACT_APP_API_URL}/interlocuteurs/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: formData.nom,
          prenom: formData.prenom,
          email_contact: formData.email.toLowerCase(),
          telephone: formData.telephone,
          fonction: formData.fonction
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      setIsConfirmUpdateModalOpen(false);
      setIsEditModalOpen(false);

      // Sauvegarder l'onglet actif avant de recharger
      localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
      // Recharger la page
      window.location.reload();

    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = (userToReset: PrestataireUser) => {
    setSelectedUser(userToReset);
    setIsResetPasswordModalOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${selectedUser.id}/reset-password`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la réinitialisation du mot de passe');
        }

        alert(`Un email de réinitialisation a été envoyé à ${selectedUser.email}`);
        setIsResetPasswordModalOpen(false);
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: selectedUser.email.toLowerCase() })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la réinitialisation du mot de passe');
      }

      alert(`Un email de réinitialisation a été envoyé à ${selectedUser.email}`);
      setIsResetPasswordModalOpen(false);

    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Supprimer un utilisateur
  const handleDeleteUser = async (userId: string | number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la suppression');
        }

        alert('Membre supprimé avec succès');
        window.location.reload();
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      const response = await fetch(`${process.env.REACT_APP_API_URL}/interlocuteurs/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      alert('Utilisateur supprimé avec succès');

      // Sauvegarder l'onglet actif avant de recharger
      localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
      // Recharger la page
      window.location.reload();

    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Recherche
  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredData(userData);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = userData.filter(item =>
      item.nom.toLowerCase().includes(searchTermLower) ||
      item.prenom.toLowerCase().includes(searchTermLower) ||
      item.email.toLowerCase().includes(searchTermLower) ||
      item.telephone.toLowerCase().includes(searchTermLower)
    );

    setFilteredData(filtered);
  };

  const columns = [
    {
      field: 'nom',
      header: 'Nom / Prénom',
      renderCell: (value: string, row: PrestataireUser) => (
        <div className="user-name">
          {row.prenom} {value}
        </div>
      )
    },
    {
      field: 'email',
      header: 'Email',
      renderCell: (value: string) => (
        <div className="user-email">
          {value}
        </div>
      )
    },
    {
      field: 'telephone',
      header: 'Téléphone',
      renderCell: (value: string) => (
        <div className="user-phone">
          {value || '-'}
        </div>
      )
    },
    {
      field: 'fonction',
      header: 'Fonction',
      renderCell: (value: string) => (
        <div className="user-fonction">
          {value || '-'}
        </div>
      )
    },
    {
      field: 'role',
      header: 'Rôle',
      renderCell: (value: string) => (
        <div className="user-role">
          {value}
        </div>
      )
    },
    {
      field: 'dateMiseAJour',
      header: 'Date de mise à jour',
      renderCell: (value: string) => (
        <div className="creation-date">
          {value}
        </div>
      )
    },
    {
      field: 'actions',
      header: 'Actions',
      renderCell: (value: any, row: PrestataireUser) => {
        // FRONT-12: Afficher les actions si l'utilisateur est admin (AC ou PL)
        const isUserAdmin = isAdminRole(user?.role || '');
        // Pour les PL: les admins peuvent gérer les membres simples
        // Pour les AC: les gestionnaires peuvent gérer les interlocuteurs
        const canManageRow = row.role === 'Membre' || row.role === 'Prestataire';

        if (isUserAdmin && canManageRow) {
          return (
            <div className="actions-cell" style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                value="Modifier"
                variant="secondary"
                size="small"
                icon={<EditIcon />}
                onClick={() => handleOpenEditModal(row)}
              />
              <Button
                value="MDP"
                variant="secondary"
                size="small"
                icon={<KeyIcon />}
                onClick={() => handleResetPassword(row)}
              />
            </div>
          );
        } else {
          // Pour les autres cas, afficher un texte ou rien
          return (
            <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
              {canManageRow ? 'Non autorisé' : 'Contactez Centralis pour modification'}
            </div>
          );
        }
      }
    }
  ];

  if (loading) {
    return <div className="loading-indicator">Chargement...</div>;
  }

  if (error) {
    return <div className="error-message">Erreur: {error}</div>;
  }

  // FRONT-12: Vérifier si l'utilisateur peut créer des comptes (admin AC ou PL)
  const canCreateAccounts = isAdminRole(user?.role || '');

  return (
    <div className="gestion-comptes-prestataires">
      {/* Header avec barre de recherche et bouton créer */}
      <div className="header-actions" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div style={{ flex: '1' }}>
          <SearchBar
            placeholder="Rechercher un membre"
            onSearch={handleSearch}
          />
        </div>
        {canCreateAccounts && (
          <Button
            value="Créer un utilisateur"
            variant="primary"
            size="small"
            icon={<PlusIcon />}
            onClick={handleOpenCreateModal}
          />
        )}
      </div>

      <TableauDeBord
        data={filteredData}
        columns={columns}
        onSearch={handleSearch}
        isSelectable={false}
        selectedItems={[]}
        onSelectionChange={() => { }}
        showActionsColumn={false}
        itemsPerPage={500}
      />

      {/* Modal de création */}
      {isCreateModalOpen && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
            <div className="modal-header">
              <h2>Créer un utilisateur</h2>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Prénom *</label>
                <input
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={formErrors.email ? 'input-error' : ''}
                  required
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className={formErrors.telephone ? 'input-error' : ''}
                  placeholder="10 chiffres"
                />
                {formErrors.telephone && <span className="error-message">{formErrors.telephone}</span>}
              </div>
              <div className="form-group">
                <label>Fonction</label>
                <input
                  type="text"
                  value={formData.fonction}
                  onChange={(e) => setFormData({ ...formData, fonction: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                size="small"
                onClick={() => setIsCreateModalOpen(false)}
              />
              <Button
                value="Créer"
                variant="primary"
                size="small"
                onClick={handleCreateUser}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {isEditModalOpen && selectedUser && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
            <div className="modal-header">
              <h2>Modifier les informations</h2>
              <button onClick={() => setIsEditModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Prénom *</label>
                <input
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={formErrors.email ? 'input-error' : ''}
                  required
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className={formErrors.telephone ? 'input-error' : ''}
                  placeholder="10 chiffres"
                />
                {formErrors.telephone && <span className="error-message">{formErrors.telephone}</span>}
              </div>
              <div className="form-group">
                <label>Fonction</label>
                <input
                  type="text"
                  value={formData.fonction}
                  onChange={(e) => setFormData({ ...formData, fonction: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                size="small"
                onClick={() => setIsEditModalOpen(false)}
              />
              <Button
                value="Mettre à jour"
                variant="primary"
                size="small"
                onClick={handleConfirmUpdate}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de réinitialisation de mot de passe */}
      {isResetPasswordModalOpen && selectedUser && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h2>Réinitialiser le mot de passe</h2>
              <button onClick={() => setIsResetPasswordModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Voulez-vous envoyer un email de réinitialisation de mot de passe à <strong>{selectedUser.email}</strong> ?
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                size="small"
                onClick={() => setIsResetPasswordModalOpen(false)}
              />
              <Button
                value="Envoyer"
                variant="primary"
                size="small"
                onClick={handleConfirmResetPassword}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de mise à jour */}
      {isConfirmUpdateModalOpen && selectedUser && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
            <div className="modal-header">
              <h2>Confirmation de modification</h2>
              <button onClick={() => setIsConfirmUpdateModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                <strong>Êtes-vous sûr de vouloir modifier les informations de contact ?</strong>
              </p>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                Cette action affectera l'ensemble des coordonnées de contact des lots concernés pour cet interlocuteur.
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Refuser"
                variant="secondary"
                size="small"
                onClick={() => setIsConfirmUpdateModalOpen(false)}
              />
              <Button
                value="Valider"
                variant="primary"
                size="small"
                onClick={handleUpdateUser}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès après création */}
      {isSuccessModalOpen && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
            <div className="modal-header">
              <h2>Compte créé avec succès</h2>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.8', fontSize: '1rem', color: '#333' }}>
                Veuillez informer votre interlocuteur qu'il doit valider l'e-mail reçu afin de pouvoir définir son mot de passe de connexion à Centralis.
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Compris"
                variant="primary"
                size="small"
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  // Sauvegarder l'onglet actif avant de recharger
                  localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
                  // Recharger la page pour actualiser la liste
                  window.location.reload();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionComptesPrestataires;
